/**
 * Tax Planning Settings API service.
 *
 * Promise-based wrapper around the backend endpoints under
 * `api/v1/TaxPlanningSettings`. Mirrors the axios/base-URL/cookie-auth pattern
 * used by the rest of ClientApp (`src/api/call.js`, `services/*`, notably
 * `services/taxConfigApi.js`). All responses are wrapped in the standard
 * ResponseModel `{ message, status, data }`; helpers below unwrap `.data` and
 * throw on a non-success `status` (APIStatus.Successful === 0) so callers can
 * `await` the inner payload directly and `catch` a friendly Error.
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc typedefs.
 *
 * @typedef {Object} TaxPlanningSettingsDto
 * @property {number} id
 * @property {?string} startPromptTh @property {?string} resumePromptTh @property {?string} yearQuestionPromptTh
 * @property {?string} completedPromptTh completed-form menu copy; MUST end with the |quickReply|["เริ่มใหม่","ใช้ข้อมูลเดิม"] marker
 * @property {?string} consultCopyTh consult-mode invite copy; MUST NOT contain a |quickReply| marker (buttons appended by the backend)
 * @property {?string} startRefinePrompt @property {?string} resumeRefinePrompt @property {?string} yearQuestionRefinePrompt
 * @property {?string} completedRefinePrompt @property {?string} consultRefinePrompt
 * @property {boolean} enablePromptRefine master toggle: AI-refine each state's source message through the refine prompt
 * @property {?string} provider @property {?string} model
 * @property {?number} temperature @property {?number} topP
 * @property {?number} frequencyPenalty @property {?number} presencePenalty
 * @property {?number} maxTokens @property {?string} stopSequence
 * @property {boolean} enableInputMessageReplace @property {boolean} enableVoicePromptReplace
 * @property {boolean} enableLanguageReplace
 *
 * @typedef {Object} SaveTaxPlanningSettingsRequest
 * @property {?string} startPromptTh @property {?string} resumePromptTh @property {?string} yearQuestionPromptTh
 * @property {?string} completedPromptTh completed-form menu copy; MUST end with the |quickReply|["เริ่มใหม่","ใช้ข้อมูลเดิม"] marker
 * @property {?string} consultCopyTh consult-mode invite copy; MUST NOT contain a |quickReply| marker (buttons appended by the backend)
 * @property {?string} startRefinePrompt @property {?string} resumeRefinePrompt @property {?string} yearQuestionRefinePrompt
 * @property {?string} completedRefinePrompt @property {?string} consultRefinePrompt
 * @property {boolean} enablePromptRefine master toggle: AI-refine each state's source message through the refine prompt
 * @property {?string} provider @property {?string} model
 * @property {?number} temperature @property {?number} topP
 * @property {?number} frequencyPenalty @property {?number} presencePenalty
 * @property {?number} maxTokens @property {?string} stopSequence
 * @property {boolean} enableInputMessageReplace @property {boolean} enableVoicePromptReplace
 * @property {boolean} enableLanguageReplace
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === API_STATUS_SUCCESS) {
      return body.data;
    }
    const err = new Error(body.message || 'Request failed');
    err.status = body.status;
    err.data = body.data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/TaxPlanningSettings';

export const taxPlanningSettingsApi = {
  /** @returns {Promise<TaxPlanningSettingsDto>} the global settings (get-or-create) */
  getSettings() {
    return get(`${ROOT}`);
  },
  /**
   * Upsert the global Tax Planning settings.
   * @param {SaveTaxPlanningSettingsRequest} body
   * @returns {Promise<TaxPlanningSettingsDto>}
   */
  saveSettings(body) {
    return post(`${ROOT}/save`, body);
  },
};

export default taxPlanningSettingsApi;
